import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { IMG } from "@/lib/images";
import { SITE_URL } from "@/config/site";
import SEO from "@/components/SEO";
import VoiceCard from "@/components/VoiceCard";
import { ArrowRight, Play, Sparkles, Users, Globe2, Clock, Radio, Mic, Search } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { dutchSlugForLanguage } from "@/config/childVoices";

const TRUST = [
  { icon: Globe2, label: "30+ talen" },
  { icon: Users, label: "Echte kinderacteurs" },
  { icon: Radio, label: "Studio's wereldwijd" },
  { icon: Clock, label: "Regie op afstand" },
];

export default function Home() {
  const { c } = useContent();
  const [featured, setFeatured] = useState([]);
  const [langs, setLangs] = useState([]);
  const [heroBg, setHeroBg] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.get("/voices", { params: { featured: true, voice_type: "human" } }).then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
    api.get("/languages").then((r) => setLangs(r.data)).catch(() => {});
    api.get("/embeds/hero_bg").then((r) => setHeroBg(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Organization", name: "VoiceOverKids Agency",
    url: SITE_URL, email: "bookings@voiceoverkids.agency",
    telephone: "+31712032441", address: { "@type": "PostalAddress", addressLocality: "Leiden", addressCountry: "NL" },
  };

  return (
    <>
      <SEO title="Echte kinderen. Echte stemmen. Wereldwijd." path="/" jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-ink text-white">
        <div className="absolute inset-0">
          {(() => {
            const custom = heroBg && heroBg.html;
            const src = custom ? mediaUrl(heroBg.html) : IMG.heroMic;
            return custom && heroBg.title === "video"
              ? <video src={src} autoPlay muted loop playsInline className="h-full w-full object-cover" />
              : <img src={src} alt="VoiceOverKids hero" className={`h-full w-full object-cover ${custom ? "opacity-100" : "opacity-30"}`} />;
          })()}
          <div className={`absolute inset-0 ${(heroBg && heroBg.html)
            ? "bg-gradient-to-r from-brand-ink/80 via-brand-ink/40 to-transparent"
            : "bg-gradient-to-r from-brand-ink via-brand-ink/90 to-brand-ink/40"}`} />
        </div>
        <div className="container-x relative py-24 md:py-36">
          <div className="max-w-3xl">
            <span className="label-eyebrow inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-white/80">
              <Sparkles size={13} /> {c("home_hero_eyebrow", "Internationale kinderstemcasting")}
            </span>
            <h1 className="mt-6 font-heading text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl md:text-7xl">
              {c("home_hero_line1", "Echte kinderen.")}<br />{c("home_hero_line2", "Echte stemmen.")}<br /><span className="text-brand-purple">{c("home_hero_line3", "Wereldwijd.")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-zinc-300">
              {c("home_hero_subtitle", "Professionele kinderstem acteurs in 30+ talen. Vind, beluister, shortlist en cast de perfecte kinderstemmen voor je productie.")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/kinderstemmen" data-testid="hero-find-voice"
                className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-7 py-4 font-bold shadow-lg shadow-violet-500/30 transition hover:bg-brand-violetHover">
                <Search size={18} /> {c("home_hero_cta1", "Vind een stem")}
              </Link>
              <Link to="/casting" data-testid="hero-cast"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 py-4 font-bold backdrop-blur transition hover:bg-white/10">
                {c("home_hero_cta2", "Wij casten voor jou")}
              </Link>
              <Link to="/ai-voices" className="inline-flex items-center gap-2 px-3 py-4 text-sm font-semibold text-brand-purple hover:text-white">
                {c("home_hero_link", "Probeer direct AI-stemmen")} <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="container-x grid grid-cols-2 gap-6 py-8 md:grid-cols-4">
          {TRUST.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3 text-zinc-700">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-brand-violet"><Icon size={19} /></span>
              <span className="font-heading text-sm font-bold">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED VOICES */}
      <section className="section-y">
        <div className="container-x">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <span className="label-eyebrow text-brand-violet">{c("home_featured_eyebrow", "Uitgelichte stemmen")}</span>
              <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight md:text-4xl">{c("home_featured_title", "Deze wil je in je productie!")}</h2>
            </div>
            <Link to="/kinderstemmen" className="hidden sm:inline-flex items-center gap-2 font-semibold text-zinc-700 hover:text-brand-violet">
              Bekijk alle <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {featured.map((t) => <VoiceCard key={t.id} talent={t} />)}
          </div>
        </div>
      </section>

      {/* TRUSTED BY BRANDS */}
      {clients.length > 0 && (
        <section className="bg-white border-y border-zinc-200 py-16">
          <div className="container-x">
            <p className="label-eyebrow text-center text-zinc-400">{c("home_brands_title", "Vertrouwd door merken wereldwijd")}</p>
            <div className="mt-8 grid grid-cols-2 items-center gap-6 sm:grid-cols-3 lg:grid-cols-6">
              {clients.map((c) => (
                <div key={c.id} className="flex h-16 items-center justify-center opacity-70 transition hover:opacity-100">
                  {c.logo_url
                    ? <img src={mediaUrl(c.logo_url)} alt={c.name} className="max-h-12 max-w-full object-contain grayscale transition hover:grayscale-0" />
                    : <span className="font-heading text-center text-sm font-bold text-zinc-500">{c.name}</span>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HUMAN vs AI */}
      <section className="bg-white section-y">
        <div className="container-x">
          <div className="mx-auto max-w-2xl text-center">
            <span className="label-eyebrow text-brand-violet">Twee manieren om de juiste jonge stem te maken</span>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight md:text-4xl">Menselijke stemmen & AI-stemmen</h2>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-zinc-200 bg-brand-paper p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-ink text-white"><Users size={22} /></span>
              <h3 className="mt-5 font-heading text-2xl font-bold">Menselijke stemmen</h3>
              <p className="mt-3 text-zinc-600">Authentieke vertolkingen door echte, geregisseerde kinderacteurs. Professionele studio's, reclames, animatie, games, nasynchronisatie en lokalisatie.</p>
              <Link to="/kinderstemmen" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-3 font-bold text-white transition hover:bg-brand-violetHover">
                Bekijk menselijke stemmen <ArrowRight size={16} />
              </Link>
            </div>
            <div className="rounded-3xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-violet text-white"><Sparkles size={22} /></span>
              <h3 className="mt-5 font-heading text-2xl font-bold">AI-stemmen</h3>
              <p className="mt-3 text-zinc-600">30 exclusieve AI-kinderstemmen in 30+ talen, met bediening voor toonhoogte, snelheid en emotie. Direct gegenereerd voor korte deadlines.</p>
              <Link to="/ai-voices" className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-violet px-6 py-3 font-bold text-brand-violet transition hover:bg-brand-violet hover:text-white">
                Probeer AI-stemmen <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CASTING PROCESS */}
      <section className="section-y">
        <div className="container-x">
          <span className="label-eyebrow text-brand-violet">Hoe casting werkt</span>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight md:text-4xl">Vijf eenvoudige stappen</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-5">
            {[["01", "Stuur je briefing", "Taal, leeftijd, stijl en deadline."],
              ["02", "Wij casten", "We selecteren geschikte kinderacteurs."],
              ["03", "Beluister & keur goed", "Bekijk voorstellen of audities op maat."],
              ["04", "Opnemen", "Professioneel geregisseerde opname."],
              ["05", "Levering", "Productieklare audio geleverd."]].map(([n, t, d]) => (
              <div key={n} className="rounded-2xl border border-zinc-200 bg-white p-5">
                <span className="font-mono text-sm font-bold text-brand-violet">{n}</span>
                <h3 className="mt-2 font-heading text-lg font-bold">{t}</h3>
                <p className="mt-1 text-sm text-zinc-500">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LANGUAGES */}
      {langs.length > 0 && (
        <section className="bg-white section-y">
          <div className="container-x">
            <span className="label-eyebrow text-brand-violet">Talen</span>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight md:text-4xl">Stemmen per taal</h2>
            <div className="mt-8 flex flex-wrap gap-3">
              {langs.map((l) => (
                <Link key={l.slug} to={`/kinderstemmen/${dutchSlugForLanguage(l.language)}`} data-testid={`lang-chip-${l.slug}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-5 py-2.5 font-semibold text-zinc-700 transition hover:border-brand-violet hover:text-brand-violet">
                  {l.language} <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-500 group-hover:bg-violet-100">{l.count}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STUDIO */}
      <section className="section-y">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <div className="overflow-hidden rounded-3xl">
            <img src={IMG.console} alt="Professionele opnamestudio mengtafel" className="h-full w-full object-cover" loading="lazy" />
          </div>
          <div>
            <span className="label-eyebrow text-brand-violet">Studio & opname op afstand</span>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight md:text-4xl">Geregisseerde sessies, overal</h2>
            <p className="mt-4 text-zinc-600">Onze eigen opnamestudio in Nederland, plus partnerstudio's wereldwijd. Verbind live via Source Connect, Session Link Pro, Cleanfeed, Skype of Zoom voor regie op afstand — met een gespecialiseerde regisseur die het beste uit ieder kind haalt.</p>
            <Link to="/studio" className="mt-6 inline-flex items-center gap-2 font-bold text-brand-violet hover:gap-3 transition-all">
              Ontdek de studio <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-ink text-white">
        <div className="container-x py-20 text-center">
          <Mic className="mx-auto mb-4 text-brand-purple" size={34} />
          <h2 className="font-heading text-4xl font-black tracking-tighter md:text-5xl">Niet de juiste stem gevonden?</h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-zinc-300">Stuur ons je script en briefing. Wij casten de juiste kinderen voor je project.</p>
          <Link to="/casting" data-testid="final-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-violet px-8 py-4 font-bold shadow-lg shadow-violet-500/30 transition hover:bg-brand-violetHover">
            Start een casting <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
